import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Zap } from "lucide-react";
import { HitAvatar } from "@/components/avatar/HitAvatar";
import { RankName, frameOf } from "@/components/avatar/RankPanel";
import { Badge, ClockPill, Label, CardPhoto, stageBg, money } from "@/components/auction/AuctionUI";

import { CONDITIONS, condName } from "@/lib/conditions";

export const GAME_LABELS = { pokemon: "Pokémon", yugioh: "Yu-Gi-Oh!", riftbound: "Riftbound", other: "Otro" };
export const TYPE_LABELS = { card_single: "Carta individual", card_group: "Grupo de cartas", sealed: "Sellado" };
export const COND_LABELS = {
  ...Object.fromEntries(CONDITIONS.map((c) => [c.value, condName(c.value)])),
  SEALED: "Sellado de fábrica", OPEN: "Abierto", DAMAGED: "Caja dañada",
};
export const LANG_LABELS = { es: "Español", en: "Inglés", jp: "Japonés", fr: "Francés", de: "Alemán", it: "Italiano", pt: "Portugués", ko: "Coreano", zh: "Chino", other: "Otro idioma" };
const COND_SHORT = { NM: "NM", LP: "LP", MP: "MP", HP: "HP", DMG: "Damaged", OPEN: "Abierto", DAMAGED: "Caja dañada" };

export function useCountdown(endsAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!endsAt) return null;
  const ms = Math.max(0, new Date(endsAt).getTime() - now);
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const hh = String(Math.floor((s % 86400) / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${d > 0 ? `${d}d ` : ""}${hh}:${mm}:${ss}`;
}

export const metaLine = (a, short = true) => [
  a.other_game_label || GAME_LABELS[a.game_slug], TYPE_LABELS[a.product_type],
  short ? COND_SHORT[a.condition] : COND_LABELS[a.condition], LANG_LABELS[a.language],
].filter(Boolean).join(" · ");

const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

// Estado visual de la tarjeta (T1–T7) a partir de los datos del backend.
export function cardState(a) {
  const turbo = a.duration_days === 1;
  const deadline = a.offer?.deadline || a.payment_deadline;
  const pdays = a.payment_deadline_days || 1;
  if (!a.is_ended) {
    const has = a.current_bid != null;
    return {
      key: has ? "active_bids" : "active_empty", turbo, tone: turbo ? "green" : "coral",
      badge: ["coral", "● Activa"], priceLabel: has ? "Puja actual" : "Aún sin pujas",
      price: has ? a.current_bid : a.starting_price, priceColor: has ? "#34D399" : "#fff",
      sub: has ? `Salida ${money(a.starting_price)}${a.bid_count != null ? ` · ${plural(a.bid_count, "puja")}` : ""}` : "Precio de salida · sé el primero",
      clockLabel: turbo ? "Turbo · 24 h" : "Termina en", clockTone: "coral", clockAt: a.ends_at,
    };
  }
  if (a.result === "awaiting_payment" || a.result === "processing_default") {
    return { key: "awaiting", tone: "amber", badge: ["amber", "Esperando pago"], priceLabel: "Estado",
      priceText: "Esperando pago", priceColor: "#fbbf24", sub: `El ganador tiene ${plural(pdays, "día")} para pagar`,
      clockLabel: "Plazo de pago", clockTone: "amber", clockAt: deadline };
  }
  if (a.result === "repechage_offered") {
    return { key: "repechage", tone: "amber", badge: ["amber", "En repechaje"], priceLabel: "Estado",
      priceText: "En repechaje", priceColor: "#fbbf24", sub: "Ofrecida al siguiente postor",
      clockLabel: "Plazo de pago", clockTone: "amber", clockAt: deadline };
  }
  if (a.result === "sold") {
    return { key: "sold", tone: "green", badge: ["green", "✓ Vendida"], priceLabel: "Vendida en",
      price: a.final_price, priceColor: "#34D399", sub: "Venta confirmada y pagada",
      clockLabel: "Estado", clockTone: "gray", clockText: "PAGADA" };
  }
  return { key: "closed", tone: "gray", dim: true, badge: ["gray", "Finalizada"],
    priceLabel: "Resultado", priceText: a.result === "unsold" ? "Sin venta" : "Sin pujas",
    priceColor: "rgba(255,255,255,.55)", sub: a.result === "unsold" ? "Ningún postor completó el pago" : "Terminó sin pujas",
    clockLabel: "Estado", clockTone: "gray", clockText: "CERRADA" };
}

const BORDER = { coral: "rgba(255,68,56,.35)", green: "rgba(52,211,153,.45)", amber: "rgba(245,158,11,.45)", gray: "rgba(255,255,255,.12)" };

export function SellerMini({ a }) {
  const s = a.seller || {};
  return (
    <div className="mt-2 flex items-center gap-[7px] text-xs text-white/60 min-w-0">
      <HitAvatar avatarId={s.avatar_id} name={s.name} size={30} rank={frameOf(s)} lite className="flex-none" testid={`auction-seller-avatar-${a.id}`} />
      {s.name && <span className="truncate">{s.name}</span>}
      {s.verified && <span className="text-[#34D399] font-bold">✓</span>}
      <RankName rank={s.rank || "common"} className="shrink-0" />
      {s.rating_avg ? <span className="text-amber-400 inline-flex items-center gap-0.5"><Star size={10} fill="currentColor" />{s.rating_avg}</span> : null}
      <span className="text-white/35 truncate">· Envío {money(a.shipping_cost_same_province)}{a.city ? ` · ${a.city}` : ""}</span>
    </div>
  );
}

function PriceFoot({ a, st }) {
  const c = useCountdown(st.clockAt);
  return (
    <div className="mx-[18px] mb-[18px] mt-3.5 pt-3.5 border-t border-white/10 flex items-end justify-between gap-3">
      <div className="min-w-0">
        <Label>{st.priceLabel}</Label>
        <div className="sx-price text-[26px] mt-1" style={{ color: st.priceColor }} data-testid={`auction-price-${a.id}`}>{st.priceText || money(st.price)}</div>
        <div className="text-xs text-white/50 mt-[5px] truncate" data-testid={`auction-sub-${a.id}`}>{st.sub}</div>
      </div>
      <div className="flex-none text-right">
        <Label>{st.clockLabel}</Label>
        <ClockPill tone={st.clockTone} className="mt-1" testid={`auction-countdown-${a.id}`}>{st.clockText || c || "—"}</ClockPill>
      </div>
    </div>
  );
}

export function AuctionCard({ a }) {
  const st = cardState(a);
  return (
    <Link to={`/subastas/${a.id}`} data-testid={`auction-card-${a.id}`} data-state={st.key}
          className="sx block overflow-hidden rounded-[22px] bg-[#101010] transition-[transform,border-color] duration-200 hover:-translate-y-0.5"
          style={{ border: `1px solid ${BORDER[st.tone]}`, opacity: st.dim ? 0.65 : 1 }}>
      <div className="relative h-[190px] flex items-center justify-center" style={stageBg(st.tone)}>
        <CardPhoto path={a.photos?.[0]} alt={a.product_name} glow={st.tone} />
        <Badge tone={st.badge[0]} className="absolute top-3.5 left-3.5" testid={`auction-status-${a.id}`}>{st.badge[1]}</Badge>
        {st.turbo && <Badge tone="turbo" className="absolute top-3.5 right-3.5" testid={`auction-turbo-${a.id}`}><Zap size={10} fill="currentColor" /> Turbo</Badge>}
      </div>
      <div className="px-[18px] pt-4">
        <div className="text-[19px] font-extrabold tracking-[-.02em] leading-[1.15] line-clamp-2" data-testid={`auction-title-${a.id}`}>{a.product_name}</div>
        <div className="mt-[5px] text-xs text-white/50 truncate">{metaLine(a)}</div>
        <SellerMini a={a} />
      </div>
      <PriceFoot a={a} st={st} />
    </Link>
  );
}

export default AuctionCard;
